// Size guide unit toggle
//
// A size guide page provides one table per unit, tagged in the page HTML:
//
//   <table class="size-guide" data-unit="cm"> … </table>
//   <table class="size-guide" data-unit="inches"> … </table>
//
// The toggle is built above the first tagged table and shows one unit at a time.
// The data-unit value is used as the button label, so the page decides the wording.
// Content with fewer than two units is left alone, which keeps the untagged size
// guides (and the size conversion table, which has no unit) working as they are.

const STORAGE_KEY = 'size-guide-unit';
const IMPERIAL_COUNTRIES = ['US'];

class SizeGuideUnits extends HTMLElement {
	connectedCallback() {
		const units = this.units;

		if (this.hasAttribute('ready') || units.length < 2) {
			return;
		}

		this.setAttribute('ready', '');
		this.renderToggle(units);
		this.unit = this.preferredUnit(units);
	}

	// The tables are read fresh each time: the theme wraps prose tables in a
	// .table-scroller by replacing them, so held references go stale
	get tables() {
		return Array.from(this.querySelectorAll('table[data-unit]'));
	}

	get units() {
		const units = this.tables.map((table) => table.dataset.unit.trim()).filter(Boolean);

		return Array.from(new Set(units));
	}

	set unit(unit) {
		this.tables.forEach((table) => {
			// The scroller is only present on content that was in the page at load,
			// so quick buy hides the table itself
			const container = table.closest('.table-scroller') || table;
			container.hidden = table.dataset.unit.trim() !== unit;
		});

		Array.from(this.querySelectorAll('[data-unit-button]')).forEach((button) => {
			button.setAttribute('aria-pressed', String(button.dataset.unitButton === unit));
		});
	}

	preferredUnit(units) {
		const stored = this.storedUnit;

		if (stored && units.includes(stored)) {
			return stored;
		}

		if (IMPERIAL_COUNTRIES.includes(window.Shopify?.country)) {
			const imperial = units.find((unit) => unit.toLowerCase().startsWith('in'));

			if (imperial) {
				return imperial;
			}
		}

		return units[0];
	}

	get storedUnit() {
		try {
			return window.localStorage.getItem(STORAGE_KEY);
		} catch (error) {
			return null; // Storage is unavailable in private browsing
		}
	}

	set storedUnit(unit) {
		try {
			window.localStorage.setItem(STORAGE_KEY, unit);
		} catch (error) {
			// Nothing to do, the choice just won't be remembered
		}
	}

	renderToggle(units) {
		const toggle = document.createElement('div');

		toggle.className = 'size-guide-units';
		toggle.setAttribute('role', 'group');
		toggle.setAttribute('aria-label', this.getAttribute('label') || 'Measurement units');

		units.forEach((unit) => {
			const button = document.createElement('button');

			button.type = 'button';
			button.className = 'size-guide-units__button';
			button.dataset.unitButton = unit;
			button.setAttribute('aria-pressed', 'false');
			button.textContent = unit;
			button.addEventListener('click', () => {
				this.unit = unit;
				this.storedUnit = unit;
			});

			toggle.append(button);
		});

		const firstTable = this.tables[0];

		(firstTable.closest('.table-scroller') || firstTable).before(toggle);
	}
}

if (!customElements.get('size-guide-units')) {
	customElements.define('size-guide-units', SizeGuideUnits);
}
